import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { useAuth } from "AuthProvider";
import { Formik, Form as FormikForm } from "formik";
import useFetch from "hooks/useFetch";
import {
  Alert,
  Button,
  Card,
  EmptyState,
  FormikTextarea,
  Icon,
  PageHeader,
  Pagination,
  Skeleton,
  useToast,
} from "stories/Admin";
import NotificationList from "./NotificationList/NotificationList";
import styles from "./NotificationPage.module.scss";

interface PushNotificationForm {
  title: string;
  imgurl: string;
  body: string;
  uid: string;
}

interface Notification {
  id: number;
  title: string;
  body: string;
  date: string;
}

const MAX_CHAR_LIMIT = 255;
const NOTIFICATIONS_PER_PAGE = 10;

const validationSchema = Yup.object().shape({
  body: Yup.string()
    .required("Melding er påkrevd")
    .max(MAX_CHAR_LIMIT, `Melding kan ikke overstige ${MAX_CHAR_LIMIT} tegn`),
});

/** Paper plane icon (not in the shared icon set). */
const SendIcon: React.FC = () => (
  <svg
    className={styles.sendIcon}
    viewBox="0 0 24 24"
    width={16}
    height={16}
    strokeWidth={2}
    aria-hidden="true"
  >
    <path d="M22 2 11 13M22 2l-7 20-4-9-9-4z" />
  </svg>
);

const NotificationPage: React.FC = () => {
  const { orgNumber, uid, mosqueName, selectedOrgNumber } = useAuth();
  const { isLoading, error, execute } = useFetch<any>();
  const {
    data: notificationsData,
    isLoading: isNotificationsLoading,
    execute: fetchNotifications,
  } = useFetch<any>();
  const toast = useToast();

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const fetchData = async () => {
      const response = await fetchNotifications(
        `/api/v1/notification/${selectedOrgNumber}`,
        "GET",
      );
      if (response) {
        const sortedNotifications = response.sort(
          (a: Notification, b: Notification) =>
            new Date(b.date).getTime() - new Date(a.date).getTime(),
        );
        setNotifications(sortedNotifications);
      }
    };
    fetchData();
  }, [selectedOrgNumber]);

  const handleSubmit = async (
    values: PushNotificationForm,
    { resetForm }: any,
  ) => {
    const currentDate = new Date().toISOString();

    const payload = {
      uid,
      orgnr: selectedOrgNumber,
      title: mosqueName,
      imgurl: "",
      body: values.body,
      date: currentDate,
    };

    const result = await execute(
      "/api/v1/notification/send-notification",
      "POST",
      payload,
    );
    resetForm();
    if (result) {
      toast.success("Push-meldingen er sendt.");
    } else {
      toast.error("Kunne ikke sende push-meldingen. Prøv igjen.");
    }
  };

  const indexOfLastNotification = currentPage * NOTIFICATIONS_PER_PAGE;
  const indexOfFirstNotification =
    indexOfLastNotification - NOTIFICATIONS_PER_PAGE;
  const currentNotifications = notifications.slice(
    indexOfFirstNotification,
    indexOfLastNotification,
  );
  const totalPages = Math.ceil(notifications.length / NOTIFICATIONS_PER_PAGE);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <>
      <PageHeader
        title="Push-meldinger"
        subtitle="Sendes til alle som følger moskeen i iBønn."
      />

      <Formik
        initialValues={{
          title: mosqueName || "",
          imgurl: "",
          body: "",
          uid: "",
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isValid, dirty, values }) => (
          <FormikForm className={styles.composer} noValidate>
            <Card title="Ny melding">
              <FormikTextarea
                name="body"
                label="Melding"
                required
                rows={3}
                placeholder="Skriv melding …"
                maxLength={MAX_CHAR_LIMIT}
                maxChars={MAX_CHAR_LIMIT}
              />
              <div className={styles.sendRow}>
                <span className={styles.help}>
                  Tittel: <b>{mosqueName}</b> (fast)
                </span>
                <Button
                  type="submit"
                  className={styles.sendButton}
                  loading={isLoading}
                  disabled={!(isValid && dirty)}
                  icon={<SendIcon />}
                >
                  Send melding
                </Button>
              </div>
              {error && (
                <Alert tone="error">
                  Kunne ikke sende push-meldingen. Prøv igjen.
                </Alert>
              )}
            </Card>

            <section className={styles.preview} aria-label="Forhåndsvisning">
              <span className={styles.previewLabel}>FORHÅNDSVISNING</span>
              <div className={styles.previewNote}>
                <span className={styles.previewIcon}>
                  <Icon name="moon" size={18} strokeWidth={2.2} />
                </span>
                <span className={styles.previewText}>
                  <span className={styles.previewTop}>
                    <b>{mosqueName}</b>
                    <span className={styles.previewTime}>nå</span>
                  </span>
                  <span className={styles.previewBody}>
                    {values.body || "Meldingen vises her."}
                  </span>
                </span>
              </div>
              <span className={styles.previewHelp}>
                Slik ser varselet ut på mobilen.
              </span>
            </section>
          </FormikForm>
        )}
      </Formik>

      <Card
        title="Sendte meldinger"
        actions={
          <span className={styles.actionsHelp}>
            {NOTIFICATIONS_PER_PAGE} per side · nyeste først
          </span>
        }
      >
        {isNotificationsLoading ? (
          <div className={styles.skeletons}>
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} height={36} />
            ))}
          </div>
        ) : error ? (
          <Alert tone="error">Kunne ikke hente meldingene.</Alert>
        ) : notifications.length > 0 ? (
          <>
            <NotificationList notifications={currentNotifications} />
            <Pagination
              page={currentPage}
              pageCount={totalPages}
              onChange={handlePageChange}
            />
          </>
        ) : (
          <EmptyState icon="bell" title="Ingen meldinger ennå">
            Meldinger du sender vises her.
          </EmptyState>
        )}
      </Card>
    </>
  );
};

export default NotificationPage;
